/**
 * SuggestionItem - A single row in the SuggestionsList.
 *
 * Shows: entity icon, refId badge, title, status chip.
 * Expand toggle reveals action.data fields as a key-value list.
 * Action buttons (Approve / Dismiss) when outstanding and canApprove.
 */

import React, { useState } from 'react';

// Entity icon helper (same logic as ProposalCard)
function getEntityIcon(suggestion) {
  const service = (suggestion?.action?.service || '').toLowerCase();
  const entityType = (suggestion?.entityType || '').toLowerCase();
  const combined = service || entityType;
  if (combined.includes('accommodation') || combined.includes('hotel')) return '🏨';
  if (combined.includes('travel') || combined.includes('flight')) return '✈️';
  if (combined.includes('place')) return '📍';
  if (combined.includes('day')) return '📅';
  return '💡';
}

function getStatusInfo(status) {
  switch (status) {
    case 'approved':  return { label: 'Approved',  className: 'suggestion-item__status--approved' };
    case 'dismissed': return { label: 'Dismissed', className: 'suggestion-item__status--dismissed' };
    case 'modified':  return { label: 'Modified',  className: 'suggestion-item__status--modified' };
    default:          return { label: 'Pending',   className: 'suggestion-item__status--outstanding' };
  }
}

const SuggestionItem = ({ suggestion, onApprove, onDismiss, onModify, canApprove = true }) => {
  const [expanded, setExpanded] = useState(false);

  const { id, refId, status = 'outstanding', action = {} } = suggestion;
  const isOutstanding = status === 'outstanding';
  const icon = getEntityIcon(suggestion);
  const statusInfo = getStatusInfo(status);
  const actionData = action.data || {};

  return (
    <div className={`suggestion-item suggestion-item--${status}`}>
      {/* Header row */}
      <div className="suggestion-item__row">
        <span className="suggestion-item__icon" aria-hidden="true">{icon}</span>
        <span className="suggestion-item__ref">#{refId}</span>
        <span className="suggestion-item__title">{action.label || 'Suggestion'}</span>
        <span className={`suggestion-item__status ${statusInfo.className}`}>
          {statusInfo.label}
        </span>
        <button
          className="suggestion-item__toggle"
          onClick={() => setExpanded((v) => !v)}
          type="button"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
        >
          {expanded ? '▲' : '▼'}
        </button>
      </div>

      {/* Expanded detail panel */}
      {expanded && (
        <div className="suggestion-item__detail">
          {action.summary && (
            <p className="suggestion-item__summary">{action.summary}</p>
          )}
          {Object.keys(actionData).length > 0 && (
            <dl className="suggestion-item__data">
              {Object.entries(actionData).map(([key, value]) => {
                const displayVal = typeof value === 'object'
                  ? JSON.stringify(value, null, 2)
                  : String(value ?? '');
                return (
                  <React.Fragment key={key}>
                    <dt className="suggestion-item__data-key">{key}</dt>
                    <dd className="suggestion-item__data-val">{displayVal}</dd>
                  </React.Fragment>
                );
              })}
            </dl>
          )}
        </div>
      )}

      {/* Action buttons */}
      {isOutstanding && canApprove && (
        <div className="suggestion-item__actions">
          <button
            className="suggestion-item__btn suggestion-item__btn--approve"
            onClick={() => typeof onApprove === 'function' && onApprove(id)}
            type="button"
          >
            Approve
          </button>
          <button
            className="suggestion-item__btn suggestion-item__btn--dismiss"
            onClick={() => typeof onDismiss === 'function' && onDismiss(id)}
            type="button"
          >
            Dismiss
          </button>
        </div>
      )}

      {isOutstanding && !canApprove && (
        <p className="suggestion-item__readonly">Owner can approve</p>
      )}
    </div>
  );
};

export default SuggestionItem;
